/* eslint-disable @typescript-eslint/ban-types */
import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { PriceType } from '../../_kernel/PriceType'
import { log } from '@s4l/monitoring'

export type AffiliateType = (typeof AFFILIATES)[number] | (string & {})
export const AFFILIATES = [
  'adwords',
  'adwordsbranding',
  'bha',
  'cozycozy',
  'criteo',
  'ebooking',
  'email_transactional',
  'email',
  'findhotel',
  'gha',
  'happypal',
  'hotelscan',
  'idealo',
  'inspiring',
  'kayak',
  'neredekal',
  'push',
  'skyscanner',
  'stayforlong',
  'tripadvisor',
  'trivago',
  'vio',
  'vipdistrict',
] as const

export const AFFILIATES_METAS = [
  'findhotel',
  'vio',
  'gha',
  'bha',
  'neredekal',
  'trivago',
  'hotelscan',
  'kayak',
  'skyscanner',
  'tripadvisor',
  'cozycozy',
]

// Constants
const AFFILIATE_STAYFORLONG = 'stayforlong'

// Enums for referer patterns organized by category
enum DirectPatterns {
  DIRECT = 'direct',
}

enum StayforlongPatterns {
  STAYFORLONG = 'stayforlong',
  S4LONG = 's4long',
  PANEL = 'panel.stayforlong',
  HELP = 'help.stayforlong',
}

enum EmailPatterns {
  MAIL = 'mail',
  GMAIL = 'gmail',
  WEBMAIL = 'webmail',
  OUTLOOK = 'outlook',
  ORANGE_FR = 'orange.fr',
  LAPOSTE = 'laposte',
  GOOGLE_GM = 'com.google.android.gm',
  MAIL_DOMAIN = 'mail.',
}

enum SearchEnginePatterns {
  GOOGLE = 'google',
  BING = 'bing',
  YAHOO = 'yahoo',
  DUCKDUCKGO = 'duckduckgo',
  ECOSIA = 'ecosia',
  QWANT = 'qwant',
  BRAVE = 'brave',
  STARTPAGE = 'startpage',
  NAVER = 'naver',
  YANDEX = 'yandex',
  BAIDU = 'baidu',
  SEZNAM = 'seznam',
}

enum AISearchPatterns {
  CHATGPT = 'chatgpt',
  PERPLEXITY = 'perplexity',
  CLAUDE = 'claude',
  GEMINI = 'gemini',
  COPILOT = 'copilot',
  PI_AI = 'pi.ai',
}

enum MetaPatterns {
  TRIVAGO = 'trivago',
  TRIPADVISOR = 'tripadvisor',
  KAYAK = 'kayak',
  SKYSCANNER = 'skyscanner',
  MOMONDO = 'momondo',
  HOTELSCOMBINED = 'hotelscombined',
  CHECKFELIX = 'checkfelix',
  CHEAPFLIGHTS = 'cheapflights',
  SWOODOO = 'swoodoo',
  VIO_COM = 'vio.com',
  BOOKING_COM = 'booking.com',
  WEGO = 'wego',
}

enum SocialMediaPatterns {
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  MESSENGER = 'messenger',
  YOUTUBE = 'youtube',
  PINTEREST = 'pinterest',
  TIKTOK = 'tiktok',
  LINKEDIN = 'linkedin',
  SNAPCHAT = 'snapchat',
}

enum OriginTypes {
  DIRECT = 'direct',
  NULL = 'null',
  STAYFORLONG = 'stayforlong',
  EMAIL = 'email',
  SEO = 'SEO',
  GEO = 'GEO',
  META = 'meta',
  SOCIAL = 'social',
  REFERRAL = 'referral',
}

export const NO_CUG_AFFILIATES: readonly AffiliateType[] = ['vipdistrict']

export const AffiliateValidation = z.object({
  value: z.union([z.enum(AFFILIATES), z.string()], { required_error: 'value is required' }),
  priceType: z.instanceof(PriceType).optional(),
})

export class Affiliate extends Model {
  static create({ value, priceType }: z.infer<typeof AffiliateValidation>) {
    const { error } = AffiliateValidation.safeParse({ value, priceType })
    if (error) {
      log.warn({
        message: '[Affiliate]: Unknown affiliate',
        metadata: { affiliate: value },
      })
    }
    return new Affiliate(value, priceType)
  }

  static default() {
    return new Affiliate('stayforlong')
  }

  constructor(
    private readonly _value: AffiliateType,
    private readonly _priceType?: PriceType
  ) {
    super()
  }

  get value() { return this._value } // prettier-ignore

  get capitalizedValue() {
    return this._value.charAt(0).toUpperCase() + this._value.slice(1)
  }

  get priceType() { return this._priceType } // prettier-ignore

  get isMeta() {
    return AFFILIATES_METAS.includes(this.value)
  }

  isDefault() {
    return this.isType('stayforlong')
  }

  isType(type: AffiliateType) {
    return this.value === type
  }

  isOneOfType(types: AffiliateType[]) {
    return types.includes(this.value)
  }

  isCug() {
    return !NO_CUG_AFFILIATES.includes(this.value as any)
  }

  toEvent({ origin }: { origin?: string }) {
    if (this._value !== AFFILIATE_STAYFORLONG) {
      return this._value
    }

    const normalizedOrigin = origin?.toLowerCase() || ''

    if (normalizedOrigin === DirectPatterns.DIRECT) {
      return OriginTypes.DIRECT
    }

    if (!origin) {
      return OriginTypes.NULL
    }

    // stayforlong internal
    if (
      normalizedOrigin.includes(StayforlongPatterns.STAYFORLONG) ||
      normalizedOrigin.includes(StayforlongPatterns.S4LONG) ||
      normalizedOrigin.includes(StayforlongPatterns.PANEL) ||
      normalizedOrigin.includes(StayforlongPatterns.HELP)
    ) {
      return OriginTypes.STAYFORLONG
    }

    // email
    if (
      normalizedOrigin.includes(EmailPatterns.MAIL) ||
      normalizedOrigin.includes(EmailPatterns.GMAIL) ||
      normalizedOrigin.includes(EmailPatterns.WEBMAIL) ||
      normalizedOrigin.includes(EmailPatterns.OUTLOOK) ||
      normalizedOrigin.includes(EmailPatterns.ORANGE_FR) ||
      normalizedOrigin.includes(EmailPatterns.LAPOSTE) ||
      normalizedOrigin.includes(EmailPatterns.GOOGLE_GM) ||
      normalizedOrigin.includes(EmailPatterns.MAIL_DOMAIN)
    ) {
      return OriginTypes.EMAIL
    }

    // GEO/AI search
    if (
      normalizedOrigin.includes(AISearchPatterns.CHATGPT) ||
      normalizedOrigin.includes(AISearchPatterns.PERPLEXITY) ||
      normalizedOrigin.includes(AISearchPatterns.CLAUDE) ||
      normalizedOrigin.includes(AISearchPatterns.GEMINI) ||
      normalizedOrigin.includes(AISearchPatterns.COPILOT) ||
      normalizedOrigin.includes(AISearchPatterns.PI_AI)
    ) {
      return OriginTypes.GEO
    }

    // meta sites
    if (
      normalizedOrigin.includes(MetaPatterns.TRIVAGO) ||
      normalizedOrigin.includes(MetaPatterns.TRIPADVISOR) ||
      normalizedOrigin.includes(MetaPatterns.KAYAK) ||
      normalizedOrigin.includes(MetaPatterns.SKYSCANNER) ||
      normalizedOrigin.includes(MetaPatterns.MOMONDO) ||
      normalizedOrigin.includes(MetaPatterns.HOTELSCOMBINED) ||
      normalizedOrigin.includes(MetaPatterns.CHECKFELIX) ||
      normalizedOrigin.includes(MetaPatterns.CHEAPFLIGHTS) ||
      normalizedOrigin.includes(MetaPatterns.SWOODOO) ||
      normalizedOrigin.includes(MetaPatterns.VIO_COM) ||
      normalizedOrigin.includes(MetaPatterns.BOOKING_COM) ||
      normalizedOrigin.includes(MetaPatterns.WEGO)
    ) {
      return OriginTypes.META
    }

    // social media
    if (
      normalizedOrigin.includes(SocialMediaPatterns.FACEBOOK) ||
      normalizedOrigin.includes(SocialMediaPatterns.INSTAGRAM) ||
      normalizedOrigin.includes(SocialMediaPatterns.MESSENGER) ||
      normalizedOrigin.includes(SocialMediaPatterns.YOUTUBE) ||
      normalizedOrigin.includes(SocialMediaPatterns.PINTEREST) ||
      normalizedOrigin.includes(SocialMediaPatterns.TIKTOK) ||
      normalizedOrigin.includes(SocialMediaPatterns.LINKEDIN) ||
      normalizedOrigin.includes(SocialMediaPatterns.SNAPCHAT)
    ) {
      return OriginTypes.SOCIAL
    }

    // SEO/search engines
    if (
      normalizedOrigin.includes(SearchEnginePatterns.GOOGLE) ||
      normalizedOrigin.includes(SearchEnginePatterns.BING) ||
      normalizedOrigin.includes(SearchEnginePatterns.YAHOO) ||
      normalizedOrigin.includes(SearchEnginePatterns.DUCKDUCKGO) ||
      normalizedOrigin.includes(SearchEnginePatterns.ECOSIA) ||
      normalizedOrigin.includes(SearchEnginePatterns.QWANT) ||
      normalizedOrigin.includes(SearchEnginePatterns.BRAVE) ||
      normalizedOrigin.includes(SearchEnginePatterns.STARTPAGE) ||
      normalizedOrigin.includes(SearchEnginePatterns.NAVER) ||
      normalizedOrigin.includes(SearchEnginePatterns.YANDEX) ||
      normalizedOrigin.includes(SearchEnginePatterns.BAIDU) ||
      normalizedOrigin.includes(SearchEnginePatterns.SEZNAM)
    ) {
      return OriginTypes.SEO
    }

    // default to referral
    return OriginTypes.REFERRAL
  }

  toJSON() {
    return {
      value: this.value,
      priceType: this.priceType?.value,
    }
  }
}
